/* ==========================================================================
   SlipperSpot — storefront redesign behaviour.

   Deliberately dependency-free and small: the homepage budget is under 120
   requests and 700 KB, so nothing here loads a framework. Everything degrades
   to a working server-rendered page if this file fails.
   ========================================================================== */
(function () {
  'use strict';

  var AR_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  var isArabic = document.documentElement.lang === 'ar';

  function digits(value) {
    var s = String(value);
    if (!isArabic) return s;
    return s.replace(/[0-9]/g, function (d) {
      return AR_DIGITS[+d];
    });
  }

  function fill(template, n) {
    return String(template || '').replace('[n]', digits(n));
  }

  /* ---------------------------------------------------------------- toast */

  var toastTimer = null;

  function toast(message) {
    if (!message) return;
    var rail = document.querySelector('.ssr-toastrail');
    if (!rail) {
      rail = document.createElement('div');
      rail.className = 'ssr-toastrail';
      document.body.appendChild(rail);
    }
    rail.innerHTML = '';
    var pill = document.createElement('div');
    pill.className = 'ssr-toast';
    pill.setAttribute('role', 'status');
    pill.textContent = message;
    rail.appendChild(pill);
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      rail.innerHTML = '';
    }, 2200);
  }

  /* ----------------------------------------------------------- cart count */

  function setCartCount(count) {
    var el = document.querySelector('[data-ssr-cart-count]');
    if (!el) return;
    el.textContent = digits(count);
    el.classList.remove('is-popping');
    void el.offsetWidth;
    el.classList.add('is-popping');
  }

  function refreshCartCount() {
    return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { Accept: 'application/json' },
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        setCartCount(cart.item_count);
        return cart;
      });
  }

  /* ------------------------------------------------- collection controls */

  var DENSITY_KEY = 'ssr:density';

  function initCollection() {
    var root = document.querySelector('[data-ssr-collection]');
    if (!root) return;

    // Density. The pre-paint value is applied by an inline script in the
    // layout; this only wires the buttons and keeps aria-pressed honest.
    function applyDensity(value) {
      document.documentElement.setAttribute('data-ssr-density', value);
      root.querySelectorAll('[data-ssr-density-set]').forEach(function (btn) {
        btn.setAttribute('aria-pressed', btn.getAttribute('data-ssr-density-set') === value ? 'true' : 'false');
      });
    }

    var stored = null;
    try {
      stored = localStorage.getItem(DENSITY_KEY);
    } catch (e) {
      /* private mode — fall through to the default */
    }
    applyDensity(stored === '1' ? '1' : '2');

    root.querySelectorAll('[data-ssr-density-set]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var value = btn.getAttribute('data-ssr-density-set');
        applyDensity(value);
        try {
          localStorage.setItem(DENSITY_KEY, value);
        } catch (e) {
          /* nothing to persist to */
        }
      });
    });

    // In-stock filter. Liquid cannot read arbitrary query parameters, so the
    // state lives in the URL and is applied here — it still survives share
    // and back.
    var params = new URLSearchParams(location.search);
    var on = params.get('instock') === '1';
    root.setAttribute('data-ssr-instock-only', on ? 'true' : 'false');

    var toggle = root.querySelector('[data-ssr-instock]');
    if (toggle) {
      toggle.classList.toggle('is-on', on);
      toggle.setAttribute('aria-pressed', on ? 'true' : 'false');
      toggle.setAttribute('href', on ? toggle.getAttribute('data-url-off') : toggle.getAttribute('data-url-on'));
    }

    var count = root.querySelector('[data-ssr-count]');
    if (count) {
      count.textContent = on ? count.getAttribute('data-count-instock') : count.getAttribute('data-count-all');
    }

    // Keep the in-stock filter attached when the sort link is followed.
    var sortLink = root.querySelector('[data-ssr-sortlink]');
    if (sortLink && on) {
      var href = sortLink.getAttribute('href');
      sortLink.setAttribute('href', href + (href.indexOf('?') === -1 ? '?' : '&') + 'instock=1');
    }
  }

  /* ------------------------------------------------------- size finder */

  function initSizeGuide() {
    var sheet = document.querySelector('[data-ssr-sizeguide]');
    if (!sheet) return null;

    var lastTrigger = null;

    function open(trigger) {
      lastTrigger = trigger || null;
      if (typeof sheet.showModal === 'function') sheet.showModal();
      else sheet.setAttribute('open', '');
    }

    function close() {
      if (typeof sheet.close === 'function') sheet.close();
      else sheet.removeAttribute('open');
      if (lastTrigger) lastTrigger.focus();
    }

    document.querySelectorAll('[data-ssr-sizeguide-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        open(btn);
      });
    });

    sheet.querySelectorAll('[data-ssr-sizeguide-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });

    // The dialog fills the viewport, so a click that lands on it rather than
    // on the panel is a click on the scrim.
    sheet.addEventListener('click', function (event) {
      if (event.target === sheet) close();
    });

    return { el: sheet, open: open, close: close };
  }

  // Continental sizing: one Paris point is 2/3 cm, and a shoe carries about
  // 1.5 cm of toe allowance over the foot. Holds from kids' 19 to adults' 45.
  function euFromFootLength(cm) {
    return Math.round((cm + 1.5) * 1.5);
  }

  function initSizeFinder(sheet, ctx) {
    if (!sheet) return;

    var input = sheet.el.querySelector('[data-ssr-finder-input]');
    if (!input) return;

    var cmOut = sheet.el.querySelector('[data-ssr-finder-cm]');
    var reason = sheet.el.querySelector('[data-ssr-finder-reason]');
    var badge = sheet.el.querySelector('[data-ssr-finder-badge]');
    var result = sheet.el.querySelector('[data-ssr-finder-result]');
    var note = sheet.el.querySelector('[data-ssr-finder-note]');
    var apply = sheet.el.querySelector('[data-ssr-finder-apply]');
    var d = sheet.el.dataset;
    var t = {
      cm: d.tCm,
      based: d.tBased,
      use: d.tUse,
      close: d.tClose,
      note: d.tNote,
      between: d.tBetween,
      soldout: d.tSoldout,
      none: d.tNone,
    };

    var STORE_KEY = 'ssr:foot';
    var picked = null;

    try {
      var saved = parseFloat(localStorage.getItem(STORE_KEY));
      if (saved >= +input.min && saved <= +input.max) input.value = saved;
    } catch (e) {
      /* private mode — start from the default */
    }

    function nearest(sizes, target, availableOnly) {
      var best = null;
      sizes.forEach(function (s) {
        if (!s.eu) return;
        if (availableOnly && s.qty <= 0) return;
        var d = Math.abs(s.eu - target);
        if (!best || d < best.d) best = { size: s, d: d };
      });
      return best;
    }

    function render() {
      var cm = parseFloat(input.value);
      var target = euFromFootLength(cm);

      if (cmOut) cmOut.textContent = digits(cm);
      input.setAttribute('aria-valuetext', cm + ' ' + t.cm);
      if (reason) reason.textContent = String(t.based || '').replace('[n]', digits(cm));

      var sizes = ctx.sizes();
      var match = nearest(sizes, target, false);
      picked = match ? match.size : null;

      if (!picked) {
        if (badge) badge.textContent = '—';
        if (note) note.textContent = t.none;
        if (apply) {
          apply.disabled = true;
          apply.textContent = t.close;
        }
        if (result) result.classList.remove('is-out');
        return;
      }

      if (badge) badge.textContent = picked.label;
      if (apply) {
        apply.disabled = false;
        apply.textContent = String(t.use || '').replace('[n]', picked.label);
      }

      var soldOut = picked.qty <= 0;
      if (result) result.classList.toggle('is-out', soldOut);

      if (soldOut) {
        var alt = nearest(sizes, target, true);
        if (alt) {
          note.textContent = String(t.soldout || '')
            .replace('[n]', picked.label)
            .replace('[a]', alt.size.label);
        } else {
          note.textContent = t.none;
        }
        return;
      }

      // Flag a genuine straddle: the measurement sits between two sizes that
      // are equally close. The store's own advice is to take the larger one.
      var neighbour = null;
      sizes.forEach(function (s) {
        if (!s.eu || s.label === picked.label || s.qty <= 0) return;
        var d = Math.abs(s.eu - target);
        if (Math.abs(d - match.d) < 0.5 && s.eu > picked.eu) neighbour = s;
      });

      if (neighbour) {
        note.textContent = String(t.between || '')
          .replace('[a]', picked.label)
          .replace('[b]', neighbour.label);
      } else {
        note.textContent = t.note;
      }
    }

    input.addEventListener('input', render);
    input.addEventListener('change', function () {
      try {
        localStorage.setItem(STORE_KEY, input.value);
      } catch (e) {
        /* nothing to persist to */
      }
    });

    if (apply) {
      apply.addEventListener('click', function () {
        if (picked) ctx.select(picked.label);
        sheet.close();
      });
    }

    // Availability changes with the selected colour, so recompute on open.
    document.querySelectorAll('[data-ssr-sizeguide-open]').forEach(function (btn) {
      btn.addEventListener('click', render);
    });

    render();
  }

  /* ------------------------------------------------------------ product */

  function initProduct(sheet) {
    var root = document.querySelector('[data-ssr-product]');
    if (!root) return;

    var dataEl = root.querySelector('[data-ssr-variants]');
    if (!dataEl) return;

    var data;
    var alternatives = [];
    try {
      data = JSON.parse(dataEl.textContent);
      var altEl = root.querySelector('[data-ssr-alternatives]');
      if (altEl) alternatives = JSON.parse(altEl.textContent) || [];
    } catch (e) {
      return;
    }

    var t = {
      soldout: root.dataset.tSoldout,
      notInSize: root.dataset.tNotinsize,
      add: root.dataset.tAdd,
      other: root.dataset.tOther,
      left: root.dataset.tLeft,
      sizeAvailable: root.dataset.tSizeavailable,
      added: root.dataset.tAdded,
      notify: root.dataset.tNotify,
      error: root.dataset.tError,
      adding: root.dataset.tAdding,
      addedShort: root.dataset.tAddedShort,
      addedMore: root.dataset.tAddedMore,
      buyNow: root.dataset.tBuyNow,
      goCheckout: root.dataset.tGoCheckout,
    };
    // Add button: "Adding…", then "Added ✓", then "Item added · add more"
    // while the chosen pair is in the cart. Buy now turns into "Go to
    // checkout" as soon as the cart holds anything.
    var busy = false;
    var justAdded = false;
    var addedIds = {};
    var cartItems = parseInt(root.dataset.cartCount, 10) || 0;
    var moneyPrefix = root.dataset.moneyPrefix || '';
    var moneySuffix = root.dataset.moneySuffix || '';

    function money(cents) {
      var amount = Math.round(cents / 100);
      var text = amount >= 1000 ? amount.toLocaleString('en-US') : String(amount);
      return moneyPrefix + digits(text) + moneySuffix;
    }

    var state = { size: data.initSize, colour: data.initColour, qty: 1 };

    var sizeButtons = Array.prototype.slice.call(root.querySelectorAll('[data-ssr-size]'));
    var swatches = Array.prototype.slice.call(root.querySelectorAll('[data-ssr-colour]'));
    var recovery = root.querySelector('[data-ssr-recovery]');
    var stockLine = root.querySelector('[data-ssr-stockline]');
    var stockText = root.querySelector('[data-ssr-stocktext]');
    var addBtn = root.querySelector('[data-ssr-add]');
    var buyNow = root.querySelector('[data-ssr-buynow]');
    var variantInput = root.querySelector('[data-ssr-variant-id]');
    var qtyInput = root.querySelector('[data-ssr-qty-input]');
    var qtyDisplay = root.querySelector('[data-ssr-qty-display]');
    var priceEl = root.querySelector('[data-ssr-price]');
    var compareEl = root.querySelector('[data-ssr-compare]');
    var colourName = root.querySelector('[data-ssr-colour-name]');
    var mainImage = root.querySelector('.ssr-product__img');
    var sticky = document.querySelector('[data-ssr-sticky]');
    var stickyMeta = document.querySelector('[data-ssr-sticky-meta]');
    var stickyAdd = document.querySelector('[data-ssr-sticky-add]');

    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-ssr-thumb]'));
    var tile = root.querySelector('.ssr-product__tile');
    var picked = root.querySelector('[data-ssr-picked]');
    var pickedThumb = root.querySelector('[data-ssr-picked-thumb]');
    var pickedValue = root.querySelector('[data-ssr-picked-value]');
    var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    var lastColour = state.colour;
    var lastSize = state.size;
    var sizeName = root.querySelector('[data-ssr-size-name]');
    var colourStep = root.querySelector('[data-ssr-opt="colour"]');
    var sizeStep = root.querySelector('[data-ssr-opt="size"]');

    // Replay a one-shot CSS animation by re-adding its class. The resting look
    // never depends on the animation running: every animated property already
    // sits at its end value in the base rule.
    function replay(el, cls) {
      if (!el || reduceMotion) return;
      el.classList.remove(cls);
      void el.offsetWidth;
      el.classList.add(cls);
    }

    // Setting `src` alone does nothing while a `srcset` is present: the browser
    // keeps painting the candidate it already chose, so the gallery never moved
    // off the first photo. Rebuild the srcset from the new URL rather than
    // dropping it, so the swapped-in image stays responsive.
    var SWAP_WIDTHS = [360, 540, 720, 900];

    function setMainImage(url) {
      if (!mainImage || !url) return;

      mainImage.src = url;
      if (mainImage.getAttribute('srcset')) {
        mainImage.srcset = SWAP_WIDTHS.map(function (w) {
          return url.replace(/([?&])width=\d+/, '$1width=' + w) + ' ' + w + 'w';
        }).join(', ');
      }

      thumbs.forEach(function (btn) {
        btn.setAttribute('aria-pressed', btn.getAttribute('data-ssr-thumb') === url ? 'true' : 'false');
      });
    }

    function variantFor(size, colour) {
      for (var i = 0; i < data.variants.length; i++) {
        var v = data.variants[i];
        if (v.size === size && v.colour === colour) return v;
      }
      return null;
    }

    function qtyFor(size, colour) {
      var v = variantFor(size, colour);
      return v ? v.q : 0;
    }

    // Total units left in a size across every colour — this is what decides
    // between "gone in this colour" and "gone everywhere".
    function sizeTotal(size) {
      var total = 0;
      for (var i = 0; i < data.variants.length; i++) {
        if (data.variants[i].size === size) total += data.variants[i].q;
      }
      return total;
    }

    // Units left in a colour across every size. Decides "sold out" versus
    // "not in your size" on the swatch.
    function colourTotal(colour) {
      var total = 0;
      for (var i = 0; i < data.variants.length; i++) {
        if (data.variants[i].colour === colour) total += data.variants[i].q;
      }
      return total;
    }

    function euOf(size) {
      for (var i = 0; i < sizeButtons.length; i++) {
        if (sizeButtons[i].dataset.ssrSize === size) return parseInt(sizeButtons[i].dataset.eu, 10) || 0;
      }
      return 0;
    }

    // Size chips speak the same language as the colour tiles: a tick when
    // chosen, and one state per chip with its own words and look.
    function renderSizes() {
      sizeButtons.forEach(function (btn) {
        var size = btn.dataset.ssrSize;
        var here = qtyFor(size, state.colour);
        var anywhere = sizeTotal(size);
        var selected = size === state.size;
        var sub = btn.querySelector('.ssr-size__sub');

        btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
        btn.classList.toggle('is-out', here <= 0);
        btn.classList.toggle('is-gone', here <= 0 && anywhere <= 0);
        btn.classList.toggle('is-other', here <= 0 && anywhere > 0);
        btn.classList.toggle('is-low', here > 0 && here <= 3);

        sub.className = 'ssr-size__sub';
        if (here <= 0 && anywhere <= 0) {
          sub.textContent = t.soldout;
          sub.classList.add('ssr-size__sub--gone');
        } else if (here <= 0) {
          sub.textContent = t.other;
          sub.classList.add('ssr-size__sub--other');
        } else if (here <= 3) {
          sub.textContent = fill(t.left, here);
          sub.classList.add('ssr-size__sub--low');
        } else {
          sub.textContent = '';
        }
      });

      var changed = lastSize !== state.size;
      if (sizeName && sizeName.textContent !== state.size) {
        sizeName.textContent = state.size;
        if (changed) replay(sizeName, 'is-changing');
      }
      if (changed) {
        var current = sizeButtons.filter(function (b) {
          return b.dataset.ssrSize === state.size;
        })[0];
        replay(current, 'is-picked');
      }
      lastSize = state.size;
    }

    function swatchFor(colour) {
      return swatches.filter(function (b) {
        return b.dataset.ssrColour === colour;
      })[0];
    }

    // Each swatch carries one of three states with its own words, never just a
    // faded dot: sold out in every size, not in the selected size, or low.
    function renderSwatches() {
      swatches.forEach(function (btn) {
        var colour = btn.dataset.ssrColour;
        var here = qtyFor(state.size, colour);
        var gone = colourTotal(colour) <= 0;
        var notHere = !gone && here <= 0;
        var low = !gone && here > 0 && here <= 3;
        var label = btn.querySelector('[data-ssr-swatch-state]');

        btn.setAttribute('aria-pressed', colour === state.colour ? 'true' : 'false');
        btn.classList.toggle('is-gone', gone);
        btn.classList.toggle('is-unavailable', notHere);
        btn.classList.toggle('is-low', low);

        if (label) {
          if (gone) label.textContent = t.soldout;
          else if (notHere) label.textContent = String(t.notInSize || '').replace('[n]', state.size);
          else if (low) label.textContent = fill(t.left, here);
          else label.textContent = '';
        }
      });

      var current = swatchFor(state.colour);
      var changed = lastColour !== state.colour;
      if (colourName && current && colourName.textContent !== current.dataset.name) {
        colourName.textContent = current.dataset.name;
        if (changed) replay(colourName, 'is-changing');
      }
      if (current && changed) {
        replay(current, 'is-picked');
        replay(tile, 'is-swapping');
      }
      lastColour = state.colour;
    }

    // A step's badge becomes a tick once that choice can actually be bought and
    // turns coral when it cannot, so each card says whether anything is left
    // to fix before Add to cart.
    function renderSteps(here) {
      if (colourStep) {
        var colourOk = colourTotal(state.colour) > 0;
        colourStep.classList.toggle('is-done', colourOk);
        colourStep.classList.toggle('is-blocked', !colourOk);
      }
      if (sizeStep) {
        sizeStep.classList.toggle('is-done', here > 0);
        sizeStep.classList.toggle('is-blocked', here <= 0);
      }
    }

    function pickText() {
      var parts = [];
      if (data.hasColour) {
        var sw = swatchFor(state.colour);
        parts.push(sw ? sw.dataset.name : state.colour);
      }
      if (data.hasSize) parts.push(state.size);
      return parts;
    }

    // "Your pick" reads back exactly what goes in the bag, right above the
    // button: the colour's own photo, its name and the size.
    function renderPicked(variant) {
      if (!picked) return;
      var parts = pickText();
      var key = parts.join('|');
      if (picked.dataset.key === key) return;
      var first = !picked.dataset.key;
      picked.dataset.key = key;

      if (pickedValue) {
        pickedValue.textContent = '';
        parts.forEach(function (part, i) {
          if (i) pickedValue.appendChild(document.createTextNode(' · '));
          var bdi = document.createElement('bdi');
          bdi.textContent = part;
          pickedValue.appendChild(bdi);
        });
      }

      if (pickedThumb && data.hasColour) {
        var sw = swatchFor(state.colour);
        var src = (variant && variant.img) || (sw && sw.dataset.img);
        pickedThumb.textContent = '';
        pickedThumb.style.background = '';
        if (src) {
          var img = document.createElement('img');
          img.src = src.replace(/([?&])width=\d+/, '$1width=120');
          img.alt = '';
          pickedThumb.appendChild(img);
        } else if (sw) {
          pickedThumb.style.background = sw.dataset.hex;
        }
      }

      if (!first) replay(picked, 'is-changing');
    }

    function colourChip(colour, name, hex, count) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ssr-recovery__colour';
      var dot = document.createElement('span');
      dot.className = 'ssr-recovery__dot';
      dot.style.background = hex;
      var label = document.createElement('span');
      label.textContent = name + ' · ' + fill(t.left, count);
      btn.appendChild(dot);
      btn.appendChild(label);
      btn.addEventListener('click', function () {
        state.colour = colour;
        state.qty = 1;
        render();
      });
      return btn;
    }

    function renderRecovery(here, anywhere) {
      recovery.innerHTML = '';
      recovery.hidden = true;
      if (here > 0) return;

      var panel = document.createElement('div');
      panel.className = 'ssr-recovery';

      // A — the size exists, just not in this colour.
      if (anywhere > 0) {
        var msg = document.createElement('div');
        msg.className = 'ssr-recovery__msg';
        msg.textContent = root.dataset.tRecoveryColour;
        panel.appendChild(msg);

        var row = document.createElement('div');
        row.className = 'ssr-recovery__colours';
        swatches.forEach(function (btn) {
          var colour = btn.dataset.ssrColour;
          var count = qtyFor(state.size, colour);
          if (count > 0) {
            var chip = colourChip(colour, btn.dataset.name, btn.dataset.hex, count);
            var chipDot = chip.querySelector('.ssr-recovery__dot');
            if (chipDot && btn.dataset.img) {
              chipDot.style.backgroundImage = 'url(' + JSON.stringify(btn.dataset.img) + ')';
              chipDot.classList.add('ssr-recovery__dot--photo');
            }
            row.appendChild(chip);
          }
        });
        panel.appendChild(row);
        recovery.appendChild(panel);
        recovery.hidden = false;
        return;
      }

      // B — the size is gone in every colour. Offer other products at about
      // the same EU size rather than dead-ending the shopper.
      var target = euOf(state.size);
      var matches = [];
      alternatives.forEach(function (alt) {
        if (matches.length >= 3) return;
        for (var i = 0; i < alt.sizes.length; i++) {
          if (Math.abs(alt.sizes[i].eu - target) <= 1) {
            matches.push({ alt: alt, size: alt.sizes[i] });
            return;
          }
        }
      });

      var head = document.createElement('div');
      head.className = 'ssr-recovery__msg ssr-recovery__msg--lg';
      head.textContent = root.dataset.tRecoverySize;
      panel.appendChild(head);

      if (matches.length) {
        var sub = document.createElement('div');
        sub.className = 'ssr-recovery__sub';
        sub.textContent = root.dataset.tRecoverySizesub;
        panel.appendChild(sub);

        var grid = document.createElement('div');
        grid.className = 'ssr-recovery__alts ssr-stagger--70';
        matches.forEach(function (match, index) {
          var card = document.createElement('a');
          card.className = 'ssr-recovery__alt';
          card.href = match.alt.url;
          card.style.animationDelay = index * 70 + 'ms';

          var tile = document.createElement('span');
          tile.className = 'ssr-recovery__altimg';
          if (match.alt.img) {
            var img = document.createElement('img');
            img.src = match.alt.img;
            img.alt = '';
            img.loading = 'lazy';
            tile.appendChild(img);
          }

          var title = document.createElement('span');
          title.className = 'ssr-recovery__alttitle';
          title.textContent = match.alt.title;

          var note = document.createElement('span');
          note.className = 'ssr-recovery__altsize';
          note.textContent = String(t.sizeAvailable || '').replace('[n]', match.size.label);

          card.appendChild(tile);
          card.appendChild(title);
          card.appendChild(note);
          grid.appendChild(card);
        });
        panel.appendChild(grid);
      }

      var notify = document.createElement('button');
      notify.type = 'button';
      notify.className = 'ssr-recovery__notify';
      notify.textContent = root.dataset.tNotifyLabel;
      notify.addEventListener('click', function () {
        toast(t.notify);
      });
      panel.appendChild(notify);

      recovery.appendChild(panel);
      recovery.hidden = false;
    }

    function render() {
      var variant = variantFor(state.size, state.colour);
      var here = variant ? variant.q : 0;
      var anywhere = sizeTotal(state.size);

      renderSizes();
      renderSwatches();
      renderRecovery(here, anywhere);
      renderPicked(variant);
      renderSteps(here);

      if (variant) {
        if (variantInput) variantInput.value = variant.id;
        if (priceEl) priceEl.textContent = money(variant.price);
        if (compareEl) {
          if (variant.compare > variant.price) {
            compareEl.textContent = money(variant.compare);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
      }

      // The photo follows the colour, not the size+colour pair. Many colours
      // don't exist in every size (e.g. Turquoise has no M11), so when the
      // exact variant is missing we still show that colour's own photo
      // instead of leaving the previous colour on screen.
      var shownImg = variant && variant.img;
      if (!shownImg && data.hasColour) {
        for (var vi = 0; vi < data.variants.length; vi++) {
          if (data.variants[vi].colour === state.colour && data.variants[vi].img) {
            shownImg = data.variants[vi].img;
            break;
          }
        }
      }
      if (shownImg) setMainImage(shownImg);

      if (here > 0 && here <= 6) {
        stockText.textContent = fill(t.left, here);
        stockLine.hidden = false;
      } else {
        stockLine.hidden = true;
      }

      state.qty = Math.max(1, Math.min(state.qty, here > 0 ? Math.min(here, 9) : 1));
      if (qtyInput) qtyInput.value = state.qty;
      if (qtyDisplay) qtyDisplay.textContent = digits(state.qty);

      var available = here > 0;
      if (addBtn) {
        var addLabel = t.add;
        if (busy) addLabel = t.adding || t.add;
        else if (justAdded) addLabel = t.addedShort || t.add;
        else if (variant && addedIds[variant.id]) addLabel = t.addedMore || t.add;
        addBtn.disabled = !available || busy;
        addBtn.classList.toggle('is-out', !available);
        addBtn.textContent = available ? addLabel : t.soldout;
      }
      if (buyNow) {
        buyNow.classList.toggle('is-hidden', !available && cartItems < 1);
        buyNow.textContent = cartItems > 0 ? t.goCheckout || t.buyNow : t.buyNow || buyNow.textContent;
      }
      if (stickyAdd) {
        stickyAdd.disabled = !available;
        stickyAdd.textContent = available ? t.add : t.soldout;
      }
      if (stickyMeta && variant) {
        stickyMeta.textContent = pickText().concat(money(variant.price)).join(' · ');
      }
    }

    sizeButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.size = btn.dataset.ssrSize;
        state.qty = 1;
        render();
      });
    });

    swatches.forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.colour = btn.dataset.ssrColour;
        state.qty = 1;
        render();
      });
    });

    root.querySelectorAll('[data-ssr-qty]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.qty += parseInt(btn.getAttribute('data-ssr-qty'), 10);
        render();
      });
    });

    // A gallery photo of a colour is a choice of that colour. Swapping only the
    // picture let shoppers look at Navy while the form still held Beige, and
    // they received the wrong pair. Tapping a colour's photo now selects it.
    thumbs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var url = btn.getAttribute('data-ssr-thumb');
        var colour = btn.getAttribute('data-ssr-thumb-colour');
        if (data.hasColour && colour && colour !== state.colour) {
          state.colour = colour;
          state.qty = 1;
          render();
        }
        setMainImage(url);
      });
    });

    function addToCart(then) {
      if (busy) return;
      var variant = variantFor(state.size, state.colour);
      if (!variant || variant.q <= 0) {
        toast(t.soldout);
        return;
      }
      var quantity = Math.min(state.qty, variant.q);
      busy = true;
      render();
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variant.id, quantity: quantity }),
      })
        .then(function (response) {
          if (!response.ok) throw new Error('add failed');
          return refreshCartCount();
        })
        .then(function (cart) {
          addedIds[variant.id] = true;
          cartItems = cart && cart.item_count ? cart.item_count : Math.max(cartItems, 1);
          if (then === 'checkout') {
            window.location.href = '/checkout';
            return;
          }
          busy = false;
          justAdded = true;
          render();
          setTimeout(function () {
            justAdded = false;
            render();
          }, 2400);
          toast([t.added].concat(pickText()).join(' · '));
        })
        .catch(function () {
          busy = false;
          render();
          toast(t.error);
        });
    }

    var form = root.querySelector('[data-ssr-form]');
    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        addToCart();
      });
    }
    if (buyNow) {
      buyNow.addEventListener('click', function () {
        if (cartItems > 0) {
          window.location.href = '/checkout';
          return;
        }
        addToCart('checkout');
      });
      // The cart can change on another page or tab: check it on arrival and
      // when the page comes back from the back/forward cache.
      var syncCartItems = function () {
        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) {
            return r.json();
          })
          .then(function (cart) {
            cartItems = cart.item_count || 0;
            render();
          })
          .catch(function () {});
      };
      syncCartItems();
      window.addEventListener('pageshow', function (event) {
        if (event.persisted) syncCartItems();
      });
    }
    if (stickyAdd) {
      stickyAdd.addEventListener('click', function () {
        addToCart();
      });
    }

    // Sticky bar appears once the main CTA has scrolled out of view. Driven by
    // scroll position rather than IntersectionObserver so it still behaves in
    // embedded and non-compositing browsers, where IO callbacks never fire.
    if (sticky && addBtn) {
      // One rect read and one class toggle — cheap enough to run inline. A
      // requestAnimationFrame guard would stall permanently on any page that
      // is not compositing, leaving the bar stuck.
      var syncSticky = function () {
        sticky.classList.toggle('is-visible', addBtn.getBoundingClientRect().bottom < 0);
      };
      window.addEventListener('scroll', syncSticky, { passive: true });
      window.addEventListener('resize', syncSticky, { passive: true });
      syncSticky();
    }

    render();

    // The finder recommends against this product's real sizes, so it needs the
    // same stock view the chips use.
    initSizeFinder(sheet, {
      sizes: function () {
        return sizeButtons.map(function (btn) {
          return {
            label: btn.dataset.ssrSize,
            eu: parseInt(btn.dataset.eu, 10) || 0,
            qty: sizeTotal(btn.dataset.ssrSize),
          };
        });
      },
      select: function (label) {
        state.size = label;
        state.qty = 1;
        render();
      },
    });
  }

  /* --------------------------------------------------------------- cart */

  function initCart() {
    var root = document.querySelector('[data-ssr-cart]');
    if (!root) return;

    root.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-ssr-line]');
      if (!btn) return;
      event.preventDefault();

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: parseInt(btn.getAttribute('data-ssr-line'), 10),
          quantity: parseInt(btn.getAttribute('data-ssr-to'), 10),
        }),
      })
        .then(function (response) {
          if (!response.ok) throw new Error('change failed');
          return fetch(location.pathname + '?section_id=' + root.getAttribute('data-section-id'));
        })
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = parsed.querySelector('[data-ssr-cart]');
          if (fresh) root.replaceWith(fresh);
          refreshCartCount();
          initCart();
        })
        .catch(function () {
          window.location.reload();
        });
    });
  }

  function init() {
    initCollection();
    initProduct(initSizeGuide());
    initCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
